import { FormInstance } from 'antd';
import { TFunction } from 'i18next';
import SiteSurveyStore from '@/store/SiteSurveyForm/SiteSurveyStore';
import { UserData } from '@/store/SiteSurveyForm/constants';

export interface AccountSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  options?: { value: string; label: string }[];
}

/**
 * Build autocomplete options from customer list
 */
export const buildAccountSelectOptions = (
  userData: UserData[] | undefined,
  displayValue: string,
  isLoading: boolean,
  t: TFunction,
): AccountSelectOption[] => {
  if (isLoading) {
    return [
      {
        value: '',
        label: t('Loading...'),
        disabled: true,
        options: [],
      },
    ];
  }

  const customers = userData || [];
  const filteredCustomers = displayValue
    ? customers.filter(customer => customer.customerName.toLowerCase().includes(displayValue.toLowerCase()))
    : customers;

  const options = filteredCustomers
    .map(customer => ({
      value: customer.customerId,
      label: customer.customerName,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return options.length
    ? [
        {
          label: t('Ecoplant Customer'),
          disabled: false,
          value: 'ecoplant',
          options,
        },
      ]
    : [
        {
          value: '',
          label: t('No results found'),
          disabled: true,
          options: [],
        },
      ];
};

export const handleAccountSelect = (
  option: AccountSelectOption,
  siteSurveyStore: SiteSurveyStore,
  form: FormInstance,
): void => {
  siteSurveyStore.updateCustomerInformationData({
    account: option.value,
    newCustomerName: undefined,
  });

  form.setFieldValue('account', option.value);
  form.setFieldValue('newCustomerName', undefined);
  form.setFieldValue('customerDisplayName', option.label);
};

/**
 * Initialize display value from store or form
 */
export const initializeDisplayValue = (siteSurveyStore: SiteSurveyStore): string => {
  const accountFieldValue = siteSurveyStore.customerInformationData.account;
  const storeCustomerName = siteSurveyStore.customerInformationData.newCustomerName;

  if (accountFieldValue) {
    const customer = siteSurveyStore.userData?.find(c => c.customerId === accountFieldValue);
    if (customer) {
      return customer.customerName;
    }
  } else if (storeCustomerName) {
    return storeCustomerName;
  }

  return '';
};

/**
 * Handle autocomplete value change
 */
export const handleAccountChange = (value: string, form: FormInstance, siteSurveyStore: SiteSurveyStore): void => {
  const customer = siteSurveyStore.userData?.find(c => c.customerName === value);

  if (!customer) {
    form.setFieldValue('account', undefined);
    form.setFieldValue('newCustomerName', value);
    form.setFieldValue('customerDisplayName', value);

    siteSurveyStore.updateCustomerInformationData({
      account: undefined,
      newCustomerName: value,
    });
  }
};

/**
 * Handle blur event - attempt to match entered text to existing customer
 */
export const handleAccountBlur = (displayValue: string, form: FormInstance, siteSurveyStore: SiteSurveyStore): void => {
  if (siteSurveyStore.customerInformationData.account) {
    return;
  }

  const trimmedValue = displayValue.trim();

  const customer = siteSurveyStore.userData?.find(c => c.customerName.toLowerCase() === trimmedValue.toLowerCase());

  if (customer) {
    form.setFieldValue('account', customer.customerId);
    form.setFieldValue('customerDisplayName', customer.customerName);
    form.setFieldValue('newCustomerName', undefined);

    siteSurveyStore.updateCustomerInformationData({
      account: customer.customerId,
      newCustomerName: undefined,
    });
  } else if (trimmedValue !== displayValue) {
    form.setFieldValue('customerDisplayName', trimmedValue);

    siteSurveyStore.updateCustomerInformationData({
      newCustomerName: trimmedValue,
    });
  }
};
